<script setup>
import isEqual from 'lodash/isEqual'

const user = useStrapiUser()
const apiVacancy = useApiVacancy()
const paginator = usePaginator(getResults, 'vacancies')

const sort = ref('match:DESC')
const filters = ref([])
const search = ref('')
const disableTagAndGeoFilters = ref(true)
const companyFilter = ref(true)
const companyQuery = ref('')
// Needed to reset the loader
const infiniteLoaderIdentifier = ref(Math.random(999999))

// Need to wrap this for better DX in template and dev tools
const meta = computed(() => paginator.meta.value)
const vacancies = computed(() => paginator.results.value)

const isCompany = computed(() => user.value?.type === 'company')

const heroSubtitle = computed(() => {
  const total = meta.value?.pagination?.total || 0
  return total === 1 ? `${total} resultaat` : `${total} resultaten`
})

const searchfilter = computed(() => {
  if (search.value) {
    return {
      title: {
        $containsi: search.value,
      },
    }
  }
  return {}
})

const formattedFilters = computed(() => {
  if (filters.value.length === 0) return {}
  const groups = []
  for (const filter of filters.value) {
    const group = groups.find((g) => g.id === filter.parent.id)
    if (group) {
      group.filters.push(filter)
    } else {
      groups.push({
        id: filter.parent.id,
        filters: [filter],
      })
    }
  }

  return {
    $and: groups.map((group) => ({
      tags: {
        id: {
          $in: group.filters.map((f) => f.id),
        },
      },
    })),
  }
})
const query = computed(() => ({
  filters: {
    $and: [
      searchfilter.value,
      formattedFilters.value,
      ...(companyQuery.value
        ? [{ company: { slug: { $eq: companyQuery.value } } }]
        : []),
      ...(isCompany.value && companyFilter.value
        ? [{ company: { id: { $eq: user.value?.company?.id } } }]
        : []),
      ...(user?.value?.lockedByReferralFromCompany
        ? [
            {
              company: {
                id: { $eq: user?.value?.lockedByReferralFromCompany?.id },
              },
            },
          ]
        : []),
    ],
  },
  sort: sort.value,
  useGeoFilter: !disableTagAndGeoFilters.value,
  useTagFilter: !disableTagAndGeoFilters.value,
}))
watchDebounced(query, resetPaginatorAndLoader, { debounce: 300 })

async function getResults(page, query) {
  return await apiVacancy.getVacancies({
    ...query,
    pagination: {
      page,
      pageSize: 25,
    },
  })
}

function resetPaginatorAndLoader(newVal, oldVal) {
  if (isEqual(newVal, oldVal)) return
  paginator.reset(query.value)
  infiniteLoaderIdentifier.value = Math.random(999999)
}

onMounted(() => {
  paginator.start(query.value)
})
</script>

<template>
  <div>
    <OverviewHero title="Openstaande vacatures" :subtitle="heroSubtitle" />
    <Container>
      <div class="lg:flex">
        <VacanciesOverviewFilter
          v-model:filters="filters"
          v-model:search="search"
          v-model:disable-tag-and-geo-filters="disableTagAndGeoFilters"
          v-model:company-filter="companyFilter"
          v-model:company-query="companyQuery"
        />
        <div class="w-full lg:w-2/3">
          <VacanciesOverviewToolbar v-model:sort="sort" />
          <div v-if="vacancies.length > 0">
            <VacanciesOverviewRow
              v-for="(vacancy, index) in vacancies"
              :key="index"
              :vacancy="vacancy"
            />
          </div>
          <ClientOnly>
            <InfiniteLoader
              :identifier="infiniteLoaderIdentifier"
              class="mt-8"
              @infinite="paginator.next"
            />
          </ClientOnly>
        </div>
      </div>
    </Container>
  </div>
</template>
